import React, { memo, useCallback, useContext, useEffect, useRef, useState } from "react";
import "./style.css";

import { Button, Radio, RadioGroup, Spinner, Stack, useToast } from "@chakra-ui/react";
import { CustomPopUpModal, PopUpModal, Title } from "../../Atoms";
import { RideMap } from "../../Molecules";

import { getStatLabel, getItemFromLocalStorage } from "../../../Library/helpers";
import { calculateBearing, computeTLStats, computeWirelessStats, sphericalCosinus } from "../../../Library/geodist";
import { DEVICE_HWTYPS, SHARING_PATH } from "../../../Library/constants";
import { LinkIcon, ShareIcon, SuccessIcon } from "../../../Assets/images/svgs";

import config from "../../../config";
import { useSearchParams } from "react-router-dom";
import { StoreContext } from "../../../Library/contexts";
import { actions } from "react-table";
const { communityWebsite } = config;

const ONE_DAY = 86400000;

const DAILY_STAT_TYPES = [
  {  title: "Ride Start Time", format: "time",       key: "sdt"  },
  {  title: "Total Distance",  format: "distance",   key: "dst"  },
  {  title: "Average Speed",   format: "speed"   ,   key: "avs"  },
  {  title: "Top Speed",       format: "speed"   ,   key: "tps"  },
  {  title: "Running Time",    format: "duration",   key: "runningTime" },
  {  title: "Total Duration",  format: "duration",   key: "dur"  },
];

const DUMMY_STATS = { sdt: 0, dst: 0, avs: 0, tps: 0, dur: 0 };

const selectDurationLabelVals = [
  { label: "2 hours", val: 2 },
  { label: "4 hours", val: 4 },
  { label: "8 hours", val: 8 },
  { label: "Until Stopped", val: -1 },
]

const TodayRide = memo(function TodayRide({ vid, vehicleStats, vehicleDetails, devType }) {
  const toast = useToast();
  const isUser = useRef(getItemFromLocalStorage());

  const [ isLoading, setIsLoading ] = useState(true);
  const [ rideData, setRideData ] = useState(vehicleStats[0] || DUMMY_STATS);
  const pingData = useRef([]);
  const pathData = useRef([]);
  const [ searchParams ] = useSearchParams();
  const { userVehicles, vehicleSharingMap, setVehicleSharingMap } = useContext(StoreContext);
  const selectedDuration = useRef("");

  const [ mapCenter, setMapCenter ] = useState(null);
  const [ showSharingModal, setShowSharingModal ] = useState({});
  const [ customPopupContent, setCustomPopupContent ] = useState({});
  const [ mapDragged, setMapDragged ] = useState(false);
  const latestPing = useRef({});

  const isMapInteracted = useRef(false);

  useEffect(() => {
    const fetchRideData = () => {
      let ts = new Date().setHours(0, 0, 0, 0);
      let data = { vid: Number(vid), tsFr: Math.floor(ts / 1000), tsTo: Math.floor(Date.now() / 1000), orphan: false };
      if (isUser.current) {
        data.isWeb = true;
        data.fromTs = data.tsFr;
        data.toTs = data.tsTo;
        requestLastSeen();
      }
      global.WS.sendRequest({
        action: isUser.current ? "getLocEvents" : ("getWirelessPings" || "getTLLocEvents"),
        data: data,
      });
    };

    // Fetch Vehicle Details
    fetchRideData();

  }, [ toast, vid ]);

  const requestLastSeen = useCallback(() => {
    let action =  isUser.current ? "getDynamicLastSeen" : "getLastSeen";
    let data = { vids: [ vid ] };
    global.WS.sendRequest({ action, data });
  }, [ vid ]);

  const requestRealtime = useCallback(() => {
    let action = "subVeh";
    let data = { veh: [ vid ] };
    global.EE.removeEventListener(global.EE.EVENT_TYPES.RT_LOC_EVT, onRealtimeData);
    global.EE.addEventListener(global.EE.EVENT_TYPES.RT_LOC_EVT, onRealtimeData);
    global.WS.sendRequest({ action: global.EE.EVENT_TYPES.UNSUBSCRIBE_TO_VEHICLES, data });
    global.WS.sendRequest({ action, data });
  }, [ vid ]);

  const onLastSeen = useCallback((data) => {
    requestRealtime();
    if (!data || !data.length) {
      // No Last Seen Data Present
      return;
    }
    data.forEach((ping) => {
      setMapCenter({ lat: ping.lat, lng: ping.lng });
      latestPing.current = ping;
      setIsLoading(false);
    });
  }, [ requestRealtime ]);

  const onRealtimeData = useCallback((ping) => {
    setRideData((old) => {
      if(ping.lat && ping.lng && !isMapInteracted.current) setMapCenter({ lat: ping.lat, lng: ping.lng });
      if(ping.ts > latestPing.current.ts) latestPing.current  = ping;
      // Checking for Same Vehicle
      if (ping.vid !== vid) return old;

      const lastPing = pathData.current[pathData.current.length - 1];
      if (ping.ts <= old.ts || !lastPing) return old;

      if (!lastPing && ping.lat && ping.lng) {
        pathData.current.push(ping);
        return { ...old, tps: Math.max(old.tps, ping.spd) };
      }

      let dst = old.dst, dur = old.dur, runningTime = old.runningTime;
      if (lastPing.ignition && ping.lat && ping.lng) {
        dst += sphericalCosinus(lastPing.lat, lastPing.lng, ping.lat, ping.lng);
        dur += (ping.ts - lastPing.ts);
        if(ping.spd > 2) runningTime += 1;
      }

      let avs = ((dst / dur) * (18 / 5));
      if(lastPing.spd > 5) ping.direction = calculateBearing(lastPing.lat, lastPing.lng, ping.lat, ping.lng)

      if(ping.lat && ping.lng) {
        pathData.current.push(ping);
      }
      return { ...old, tps: Math.max(old.tps, ping.spd), dst, dur, avs, runningTime };
    })

  }, [ vid ]);

  const onRideData = useCallback((ping) => {
    if (ping.ts !== 0) {
      pingData.current.push(ping);
    } else {
      global.EE.removeEventListener(global.EE.EVENT_TYPES.ON_VEHICLE_DATA, onRideData);
      global.EE.addEventListener(global.EE.EVENT_TYPES.ON_VEHICLE_DATA, onRideData);

      pingData.current.sort((a, b) => (a.ts - b.ts));

      let dailyRideFunc = devType === DEVICE_HWTYPS.Wireless ? computeWirelessStats : computeTLStats;
      let rideData = dailyRideFunc(pingData.current);

      // If !Wireless Device
      // pingData.filter((ping) => ping.ignition);

      pingData.current.forEach((ping) => {
        if (ping.lat && ping.lng) pathData.current.push(ping);
      });

      global.gRideData = rideData;
      if (!rideData) {
        rideData = DUMMY_STATS;
        // Don't stop Loading here as there is nothing to show on Map yet
        if(!isUser.current) requestLastSeen();
        return;
      }
      setRideData(rideData);
      setIsLoading(false);

      if(!isUser.current) requestRealtime();
    }
  }, [ requestLastSeen, requestRealtime, onRealtimeData ]);

  const handleLinkCopySuccess = (linkToCopy) => {
    navigator.clipboard.writeText(linkToCopy);

    setCustomPopupContent({
      open: true,
      modalBody: (
        <div className="link-copy-success">
          <SuccessIcon />
          <p>Link Copied</p>
        </div>
      ),
      onModalClose: () => setCustomPopupContent({ open: false }),
    });

    setTimeout(() => {
      setCustomPopupContent({ open: false });
    }, 2000)
  }

  const createSharingLink = (data) => {
    if (!data.tkn) return;

    let newSharingMap = {...vehicleSharingMap};
    newSharingMap[data.vid] = data.tkn;
    setVehicleSharingMap(newSharingMap);

    let sharingUrl = `${communityWebsite}${SHARING_PATH}?tkn=${data.tkn}`;
    setCustomPopupContent({
      open: true,
      modalBody: (
        <div>
          <p className="copy-link-text">Copy link to share live location</p>
          {/*<p className="copy-link-note">This link will be valid till tomorrow</p>*/}
        </div>
      ),
      confirmBtn: (
        <div className="copy-link-button">
          <LinkIcon />
          <p>Copy Link</p>
        </div>
      ),
      onConfirm: () => handleLinkCopySuccess(sharingUrl),
      onModalClose: () => setCustomPopupContent({ open: false }),
    });
  }

  const handleStopSharing = (data) => {
    if (data.tkn) {
      let newSharingMap = { ...vehicleSharingMap };
      delete newSharingMap[data.vid];
      setVehicleSharingMap(newSharingMap);

      setCustomPopupContent({
        open: true,
        modalBody: (
          <div className="link-copy-success">
            <SuccessIcon />
            <p>Sharing Stopped</p>
          </div>
        ),
        onModalClose: () => setCustomPopupContent({ open: false }),
      });

      setTimeout(() => {
        setCustomPopupContent({ open: false });
      }, 2000)
    } else {
      setCustomPopupContent({
        open: true,
        modalBody: (
          <div className="link-copy-success error-msg">
            <p>Something went wrong! Please try again later.</p>
          </div>
        ),
        onModalClose: () => setCustomPopupContent({ open: false }),
      });
    }
  }

  const onWsReady = () =>{
    requestRealtime();
    global.EE.addEventListener(global.EE.EVENT_TYPES.RT_LOC_EVT, onRealtimeData);
  }

  useEffect(()=>{
    return () =>{
      let data = { veh: [ vid ] };
      if(global.WS) global.WS.sendRequest({ action: global.EE.EVENT_TYPES.UNSUBSCRIBE_TO_VEHICLES, data });
    };
  },[]);

  useEffect(() => {
    global.EE.addEventListener(global.EE.EVENT_TYPES.ON_VEHICLE_DATA, onRideData);
    global.EE.addEventListener(global.EE.EVENT_TYPES.ON_LAST_SEEN, onLastSeen);
    global.EE.addEventListener(global.EE.EVENT_TYPES.TIMELINE_START_SHARING, createSharingLink);
    global.EE.addEventListener(global.EE.EVENT_TYPES.TIMELINE_STOP_SHARING, handleStopSharing);
    global.EE.addEventListener(global.EE.EVENT_TYPES.WS_CONNECTED, onWsReady);

    return () => {
      global.EE.removeEventListener(global.EE.EVENT_TYPES.ON_VEHICLE_DATA, onRideData);
      global.EE.removeEventListener(global.EE.EVENT_TYPES.RT_LOC_EVT, onRealtimeData);
      global.EE.removeEventListener(global.EE.EVENT_TYPES.ON_LAST_SEEN, onLastSeen);
      global.EE.removeEventListener(global.EE.EVENT_TYPES.TIMELINE_START_SHARING, createSharingLink);
      global.EE.removeEventListener(global.EE.EVENT_TYPES.TIMELINE_STOP_SHARING, handleStopSharing);
      global.EE.removeEventListener(global.EE.EVENT_TYPES.WS_CONNECTED, onWsReady);
    };
  }, [ onRideData, onRealtimeData, onLastSeen ]);

  const generateSharingLink = () => {
    if (global.WS) {
      let vid = searchParams.get("idx");
      let fname = getItemFromLocalStorage("fn");
      let userHandle = getItemFromLocalStorage("uh");
      let sharingData = { liveSharing: true }
      if (vid && Number(vid)) {
        sharingData.vid = Number(vid);
        let vehicle = {};
        if (userVehicles && userVehicles.length) vehicle = userVehicles.find(veh => veh.idx === sharingData.vid);
        if (vehicle && vehicle.typ) sharingData.vtyp = vehicle.typ;
      }
      if (fname) sharingData.fn = fname;
      if (userHandle) sharingData.uh = userHandle;
      if (selectedDuration.current && Number(selectedDuration.current)) sharingData.duration = Number(selectedDuration.current);

      setShowSharingModal({ open: false });
      setCustomPopupContent({
        open: true,
        modalBody: (
            <Spinner size={"xl"} thickness="4px" speed="0.65s" emptyColor="gray.500" color="white.600" />
        )
      });
      let request = { action: global.EE.EVENT_TYPES.TIMELINE_START_SHARING, data: sharingData };
      global.WS.sendRequest(request);
    }
  }

  const handleSharingModal = () => {
    if (!selectedDuration.current) return;

    setCustomPopupContent({ open: false });
    setShowSharingModal({
      open: true,
      bodyClass: "sharing-modal",
      footer: "sharing-footer",
      modalMsg: "Generate link to share this vehicle's live location",
      confirmBtn: "Generate Link",
      confirmIcon: true,
      closeBtn: "Cancel",
      onConfirm: () => generateSharingLink(),
      onModalClose: () => setShowSharingModal({ open: false }),
    });
  }

  const handleLocationSharing = () => {
    setCustomPopupContent({
      open: true,
      modalBody: (
        <div className="select-sharing-duration">
          <p className="select-sharing-duration-title">Share your live location for</p>
          <RadioGroup onChange={(val) => selectedDuration.current = val}>
            <Stack>
              {selectDurationLabelVals.map((duration) => (
               <Radio value={`${duration.val}`}>{duration.label}</Radio>
              ))}
            </Stack>
          </RadioGroup>
        </div>
      ),
      closeBtn: "Cancel",
      onModalClose: () => {setCustomPopupContent({ open: false }); selectedDuration.current = "";},
      confirmBtn: "Share",
      onConfirm: () => handleSharingModal(),
    });
  }

  const requestStopSharing = () => {
    if (global.WS) {
      // {"tkn": "8be860cf0a41895c900803aafd751a536e686b65757bb75bb049739609114859", "vid": 49614}
      setCustomPopupContent({
        open: true,
        modalBody: (
          <Spinner size={"xl"} thickness="4px" speed="0.65s" emptyColor="gray.500" color="white.600" />
        ),
      });
      let request = { action: global.EE.EVENT_TYPES.TIMELINE_STOP_SHARING, data: { tkn: vehicleSharingMap[vid], vid: vid }};
      global.WS.sendRequest(request);
    }
  }

  const handleActiveSharingModal = () => {
    let sharingUrl = `${communityWebsite}${SHARING_PATH}?tkn=${vehicleSharingMap[vid]}`;

    setCustomPopupContent({
      open: true,
      modalBody: (
        <div>
          <p className="copy-link-text">Location sharing is currently active</p>
          {/*<p className="copy-link-note">This link will be valid till tomorrow</p>*/}
        </div>
      ),
      footer: "copy-link-footer",
      closeBtn: (
        <div className="copy-link-button">
          <LinkIcon />
          <p>Copy Link</p>
        </div>
      ),
      customClose: () => handleLinkCopySuccess(sharingUrl),
      confirmBtn: (
        <div className="stop-sharing-button">
          <ShareIcon />
          <p>Stop Sharing</p>
        </div>
      ),
      onConfirm: () => requestStopSharing(),
      onModalClose: () => setCustomPopupContent({ open: false }),
    });
  }

  const handleMapDrag = (removeDrag) => {
    if (removeDrag) {
      isMapInteracted.current = false;
      setMapDragged(false);
      setMapCenter({ lat: latestPing.current.lat, lng: latestPing.current.lng });
      return;
    }

    isMapInteracted.current = true;
    setMapDragged(true);
  }

  return (
    <div className="past-rides ride-details">
      <div className="card vehicle-map-card">

        <header>
          <div className="share-loc-header">
            <Title>{"Today's Stats"}</Title>
            {!vehicleSharingMap[vid]
              ? <Button leftIcon={<ShareIcon />} onClick={handleLocationSharing} className="share-location">Share Location</Button>
              : <Button leftIcon={<ShareIcon />} onClick={handleActiveSharingModal} className="share-location sharing-active">Location sharing active</Button>}
          </div>


          <div className="widget-group ride-spacer">
            {(DAILY_STAT_TYPES).map((stat) => (
              <main className="widget-main" key={stat.key}>
                <header>{stat.title}</header>
                <article>{getStatLabel(rideData[stat.key], stat.format, 1)}</article>
              </main>
            ))}
          </div>
        </header>
        <div className="past-rides today-ride ride-details">
          <RideMap isTodayRide={true} mapDragged={mapDragged} onDrag={handleMapDrag} vehicleDetails={vehicleDetails} pathTrail={pathData.current} mapCenter={mapCenter} loading={isLoading} currentPing={latestPing.current} liveRide />
        </div>
      </div>

      <PopUpModal { ...showSharingModal } />
      <CustomPopUpModal { ...customPopupContent } />
    </div>
  );
});


export default TodayRide;